import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Kanban, MessageSquare, Smartphone, Wand2, Check, Menu, X, ChevronRight, ArrowRight,
  Sparkles, Camera, ScanLine, FileText, PenLine, GraduationCap, TriangleAlert, BadgeCheck,
  BellRing, PackageSearch, MessagesSquare, CalendarClock, PhoneCall, Wrench, Handshake, Lightbulb, Ruler, Layers
} from 'lucide-react';

// Animate on scroll hook
function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <section ref={ref} id={id} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </section>
  );
}

// First contact: a free call booked over WhatsApp
const WHATSAPP_NUMBER = '393318184266';
const WHATSAPP_DISPLAY = '331 818 4266';
const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
const BOOK_CALL_URL = waLink('Ciao, vorrei prenotare la chiamata gratuita per capire cosa posso automatizzare nella mia azienda di serramenti.');
const SEND_SURVEY_URL = waLink('Ciao, vi mando la foto di un mio sopralluogo: vorrei vedere il preventivo preparato dall\'intelligenza artificiale.');

function WhatsAppIcon({ size = 20, className = '' }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.23 8.24Zm4.52-6.17c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
    </svg>
  );
}

const NAV_LINKS = [['#servizi', 'Servizi'], ['#come-lavoriamo', 'Come lavoriamo'], ['#preventivo', 'Preventivo dalle misure'], ['#faq', 'Domande']];

// Navbar
function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || open ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-[#E2E8F0]' : 'bg-transparent'}`} data-testid="navbar">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-brand">
          <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none">
            <rect x="2" y="2" width="28" height="28" rx="4" fill="#0033A0"/>
            <rect x="6" y="6" width="8" height="9" rx="1" fill="white" opacity="0.9"/>
            <rect x="18" y="6" width="8" height="9" rx="1" fill="white" opacity="0.7"/>
            <rect x="6" y="18" width="8" height="8" rx="1" fill="white" opacity="0.7"/>
            <rect x="18" y="18" width="8" height="8" rx="1" fill="white" opacity="0.5"/>
          </svg>
          <span className="font-outfit text-xl font-bold text-[#0A0F1A]">Infismart</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} className="text-sm font-medium text-[#4A5568] hover:text-[#0033A0] transition-colors">{label}</a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-[#0033A0] hover:text-[#002880] px-4 py-2 transition-colors" data-testid="navbar-login">Accedi</Link>
          <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0033A0] hover:bg-[#002880] px-5 py-2.5 rounded-lg transition-all shadow-sm hover:shadow-md" data-testid="navbar-cta">
            <WhatsAppIcon size={16} /> Prenota una chiamata
          </a>
        </div>

        {/* Mobile hamburger */}
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label={open ? 'Chiudi il menu' : 'Apri il menu'} data-testid="navbar-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2E8F0] px-4 pb-4 shadow-lg">
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="block py-3 text-base font-medium text-[#4A5568]">{label}</a>
          ))}
          <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
            <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 text-base font-semibold text-white bg-[#0033A0] py-3 rounded-lg">
              <WhatsAppIcon size={18} /> Prenota una chiamata
            </a>
            <Link to="/login" className="text-center text-base font-medium text-[#0033A0] py-3 rounded-lg border border-[#0033A0]">Accedi</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// Hero: before/after of an AI survey
const NOTE_LINES = [
  { text: 'Sig. Rossi — Via Roma 12, Como', className: 'font-bold' },
  { text: 'Sogg.  PF2  140x220  PVC bianco  x2' },
  { text: 'Camere  F2  120x140  noce  x3' },
  { text: 'TAP all.  140x230  x2' },
  { text: 'Bagno  F1  60x9?', uncertain: true },
];

const DEMO_ITEMS = [
  { name: 'Portafinestra 2 ante', detail: '1400×2200 · PVC · 2 pz', price: '2.464,00' },
  { name: 'Finestra 2 ante', detail: '1200×1400 · PVC noce · 3 pz', price: '1.692,00' },
  { name: 'Tapparella alluminio', detail: '1400×2300 · 2 pz', price: '1.004,64' },
  { name: 'Finestra 1 anta', detail: '600×900 · PVC · 1 pz', price: '135,00', uncertain: true },
];

function SurveyDemo() {
  return (
    <div className="relative grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-3" data-testid="hero-demo" aria-label="Esempio: dagli appunti del sopralluogo al preventivo prezzato con l'intelligenza artificiale">
      {/* Handwritten survey note */}
      <div className="relative bg-[#FFFDF5] rounded-lg shadow-lg border border-[#EDE6CF] p-5 sm:-rotate-2 overflow-hidden" data-testid="hero-demo-note">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent 0px, transparent 31px, #D6E2F5 31px, #D6E2F5 32px)',
          backgroundPosition: '0 14px'
        }} />
        <div className="landing-scan" aria-hidden="true" />
        <p className="relative text-[10px] font-bold uppercase tracking-[0.15em] text-[#A0AEC0] mb-2">Foglio del sopralluogo</p>
        <ul className="relative space-y-[6px]">
          {NOTE_LINES.map((l, i) => (
            <li key={i} className={`font-handwriting text-[19px] leading-[26px] text-[#1E2A78] ${l.className || ''}`}>
              {l.text}
              {l.uncertain && <span className="ml-2 align-middle text-[10px] font-sans font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">?</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* Arrow */}
      <div className="flex sm:flex-col items-center justify-center gap-2 text-[#0033A0]" aria-hidden="true">
        <div className="w-11 h-11 rounded-full bg-[#0033A0] text-white flex items-center justify-center shadow-lg shadow-[#0033A0]/30">
          <Sparkles size={20} />
        </div>
        <ArrowRight size={20} className="rotate-90 sm:rotate-0" />
      </div>

      {/* Generated quote */}
      <div className="bg-white rounded-lg shadow-xl border border-[#E2E8F0] p-5 sm:rotate-1" data-testid="hero-demo-quote">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0033A0]">Preventivo PRV-0042</p>
          <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">Cliente creato</span>
        </div>
        <ul className="divide-y divide-[#EDF2F7]">
          {DEMO_ITEMS.map((it, i) => (
            <li key={i} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0A0F1A] truncate">{it.name}</p>
                <p className="text-[11px] text-[#4A5568]">{it.detail}</p>
                {it.uncertain
                  ? <span className="inline-block mt-1 text-[10px] font-medium text-amber-800 bg-amber-100 rounded-full px-1.5 py-0.5">⚠ Misure da verificare</span>
                  : <span className="inline-block mt-1 text-[10px] font-medium text-emerald-800 bg-emerald-100 rounded-full px-1.5 py-0.5">Prezzo da listino</span>}
              </div>
              <p className="text-sm font-semibold text-[#0033A0] whitespace-nowrap">€ {it.price}</p>
            </li>
          ))}
        </ul>
        <div className="mt-2 pt-3 border-t border-[#E2E8F0] flex items-center justify-between">
          <span className="text-xs font-semibold text-[#0A0F1A]">Totale (IVA 10% incl.)</span>
          <span className="font-outfit text-lg font-bold text-[#0033A0]">€ 5.825,20</span>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 overflow-hidden" data-testid="hero-section">
      {/* Background geometric pattern */}
      <div className="absolute inset-0 bg-[#F4F6F8]">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `repeating-linear-gradient(90deg, #0033A0 0px, transparent 1px, transparent 60px),
                           repeating-linear-gradient(0deg, #0033A0 0px, transparent 1px, transparent 60px)`
        }} />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#0033A0]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#0033A0]/3 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-12 lg:gap-10 items-center">
          <div className="text-center lg:text-left">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-4" data-testid="hero-overline">
              <Sparkles size={14} /> Automazioni con l'intelligenza artificiale per serramentisti
            </p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.05] text-[#0A0F1A] mb-6" data-testid="hero-h1">
              Meno lavoro ripetitivo. Più tempo per cantieri e clienti.
            </h1>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8" data-testid="hero-subtitle">
              Preventivi dalle misure di cantiere, solleciti ai clienti, listini dei fornitori, appuntamenti di posa:
              le attività che ogni giorno ti rubano ore le automatizziamo <strong className="text-[#0A0F1A]">noi</strong>,
              con l'intelligenza artificiale, su misura per la tua azienda.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0033A0] hover:bg-[#002880] text-white font-semibold px-6 whitespace-nowrap py-3.5 rounded-lg transition-all shadow-lg shadow-[#0033A0]/20 hover:shadow-xl hover:shadow-[#0033A0]/30 hover:-translate-y-0.5" data-testid="hero-cta-primary">
                <WhatsAppIcon size={18} />
                Prenota una chiamata gratuita
              </a>
              <a href="#preventivo" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-[#0033A0] font-medium px-6 py-3.5 rounded-lg border border-[#E2E8F0] bg-white/60 hover:border-[#0033A0]/30 hover:bg-[#0033A0]/5 transition-all whitespace-nowrap" data-testid="hero-cta-secondary">
                Guarda come funziona
                <ChevronRight size={18} />
              </a>
            </div>
            <p className="text-sm text-[#4A5568] mt-4">Analisi gratuita e senza impegno. Ci scrivi su WhatsApp e fissiamo l'orario che preferisci.</p>
          </div>

          <SurveyDemo />
        </div>

        {/* Facts */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto" data-testid="hero-facts">
          {[
            { num: 'Gratis', label: 'la prima chiamata di analisi' },
            { num: '< 1 min', label: 'dalle misure al preventivo' },
            { num: 'Su misura', label: 'impostiamo tutto noi' },
            { num: 'WhatsApp', label: 'assistenza diretta' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-outfit text-2xl sm:text-3xl font-bold text-[#0033A0]">{s.num}</p>
              <p className="text-sm sm:text-base text-[#4A5568] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// The repetitive work we take off the table
const PAINS = [
  'Ricopiare le misure del sopralluogo e rifare i preventivi a mano, la sera',
  'Rincorrere i clienti che non rispondono dopo il preventivo',
  'Aggiornare a mano listini e ordini dei fornitori',
  'Rispondere sempre alle stesse richieste da sito, email e WhatsApp',
  'Tenere a mente pose, misure da riprendere e appuntamenti',
];

function Pains() {
  return (
    <Section className="py-20 sm:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Ti riconosci?</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A]">
            Le ore che se ne vanno ogni settimana
          </h2>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pains-list">
          {PAINS.map((p, i) => (
            <li key={i} className="flex items-start gap-3 bg-[#F4F6F8] rounded-xl p-5">
              <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-[#0033A0]/10 text-[#0033A0] flex items-center justify-center">
                <X size={14} />
              </span>
              <span className="text-base sm:text-lg text-[#0A0F1A] leading-snug">{p}</span>
            </li>
          ))}
        </ul>
        <p className="text-center text-base sm:text-lg text-[#4A5568] mt-8">
          Sono lavori necessari, ma ripetitivi: <strong className="text-[#0A0F1A]">li può fare l'intelligenza artificiale</strong>, mentre tu controlli e decidi.
        </p>
      </div>
    </Section>
  );
}

// Services
const SERVICES = [
  {
    icon: Ruler, title: 'Preventivo dalle misure di cantiere', featured: true,
    desc: 'Fotografi gli appunti del sopralluogo, anche scritti a mano: l\'intelligenza artificiale legge misure e prodotti, applica il tuo listino e prepara cliente e preventivo in pochi secondi.',
    link: '#preventivo', linkLabel: 'Guarda come funziona',
  },
  {
    icon: BellRing, title: 'Solleciti automatici ai clienti',
    desc: 'Dopo l\'invio del preventivo, promemoria e messaggi su WhatsApp ed email partono al momento giusto. Nessun cliente dimenticato.',
  },
  {
    icon: PackageSearch, title: 'Listini e ordini dei fornitori',
    desc: 'I PDF dei fornitori diventano prezzi nel tuo listino e righe d\'ordine, senza ricopiare codici e importi a mano.',
  },
  {
    icon: MessagesSquare, title: 'Risposte alle richieste di preventivo',
    desc: 'Le richieste che arrivano da sito, email e WhatsApp ricevono subito una risposta, e i dati del cliente sono già pronti per il sopralluogo.',
  },
  {
    icon: CalendarClock, title: 'Promemoria per pose e appuntamenti',
    desc: 'Sopralluoghi, misure da riprendere e pose: promemoria automatici per te, per la squadra e per il cliente.',
  },
];

function Services() {
  return (
    <Section id="servizi" className="py-24 sm:py-28 bg-[#F4F6F8]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Servizi</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A] mb-4">
            Cosa possiamo automatizzare per te
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Partiamo da quello che ti fa perdere più tempo. Ogni automazione la impostiamo noi, con i tuoi prodotti, i tuoi prezzi e il tuo modo di lavorare.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
          {SERVICES.map((sv, i) => {
            const Icon = sv.icon;
            return (
              <div
                key={i}
                className={`rounded-xl p-6 sm:p-8 flex flex-col ${sv.featured ? 'bg-[#0033A0] text-white shadow-xl shadow-[#0033A0]/20' : 'bg-white border border-[#E2E8F0]'}`}
                data-testid={`service-card-${i}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${sv.featured ? 'bg-white/15' : 'bg-[#0033A0]/10'}`}>
                  <Icon size={22} className={sv.featured ? 'text-white' : 'text-[#0033A0]'} />
                </div>
                {sv.featured && <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-1">Il più richiesto</p>}
                <h3 className={`font-outfit text-lg font-semibold mb-2 ${sv.featured ? 'text-white' : 'text-[#0A0F1A]'}`}>{sv.title}</h3>
                <p className={`text-base leading-relaxed flex-1 ${sv.featured ? 'text-white/85' : 'text-[#4A5568]'}`}>{sv.desc}</p>
                {sv.link && (
                  <a href={sv.link} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:underline">
                    {sv.linkLabel} <ArrowRight size={16} />
                  </a>
                )}
              </div>
            );
          })}
          <div className="rounded-xl p-6 sm:p-8 flex flex-col border-2 border-dashed border-[#0033A0]/30 bg-white/60" data-testid="service-card-custom">
            <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center mb-5">
              <Lightbulb size={22} className="text-[#B45309]" />
            </div>
            <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A] mb-2">Hai un'altra attività ripetitiva?</h3>
            <p className="text-base text-[#4A5568] leading-relaxed flex-1">Raccontacela: valutiamo insieme se e come si può automatizzare, senza impegno.</p>
            <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0033A0] hover:underline">
              <WhatsAppIcon size={16} /> Scrivici su WhatsApp
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
}

// How we work with the customer
const WORK_STEPS = [
  { icon: PhoneCall, title: 'Chiamata gratuita di analisi', desc: 'Ci racconti come lavori e dove perdi più tempo. In mezz\'ora capiamo insieme cosa conviene automatizzare per primo.' },
  { icon: Wrench, title: 'Impostiamo tutto noi', desc: 'Carichiamo il tuo listino, le tue sigle e i tuoi messaggi. Non devi configurare niente e non devi cambiare il tuo modo di lavorare.' },
  { icon: Handshake, title: 'Ti affianchiamo', desc: 'Ti mostriamo come si usa, passo per passo, e restiamo a disposizione su WhatsApp quando ti serve.' },
];

function HowWeWork() {
  return (
    <Section id="come-lavoriamo" className="py-24 sm:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Come lavoriamo</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A] mb-4">
            Tu ci spieghi il problema, noi lo risolviamo
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Non ti vendiamo un programma da imparare da solo: costruiamo le automazioni intorno alla tua azienda.
          </p>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="work-steps">
          {WORK_STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={i} className="relative bg-[#F4F6F8] rounded-xl p-6 sm:p-8">
                <span className="absolute top-6 right-6 font-outfit text-5xl font-bold text-[#0033A0]/10" aria-hidden="true">{i + 1}</span>
                <div className="w-12 h-12 rounded-lg bg-[#0033A0] text-white flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A] mb-2">{s.title}</h3>
                <p className="text-base text-[#4A5568] leading-relaxed">{s.desc}</p>
              </li>
            );
          })}
        </ol>
        <div className="text-center mt-10">
          <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#0033A0] hover:bg-[#002880] text-white font-semibold px-8 py-3.5 rounded-lg transition-all shadow-lg shadow-[#0033A0]/20" data-testid="work-cta">
            <WhatsAppIcon size={18} /> Prenota la chiamata gratuita
          </a>
        </div>
      </div>
    </Section>
  );
}

// Flagship service: from site measures to a priced quote
const STEPS = [
  {
    icon: Camera, title: 'Scatti le foto del sopralluogo',
    desc: 'Dal telefono, direttamente in cantiere: fino a 8 foto o PDF per sopralluogo, anche note del telefono o fogli scansionati.',
  },
  {
    icon: ScanLine, title: "L'intelligenza artificiale legge appunti e schizzi",
    desc: 'Riconosce le sigle di settore, le misure in cm o mm, i disegni a mano con ante e aperture. Se un numero è poco leggibile te lo segnala invece di inventarlo.',
  },
  {
    icon: FileText, title: 'Il preventivo è pronto, col tuo listino',
    desc: 'Cliente creato, prodotti abbinati al catalogo e prezzati con il tuo listino, compresi accessori come cielini e davanzali. Controlli, ritocchi se serve e invii il PDF.',
  },
];

function QuoteFromMeasures() {
  return (
    <Section id="preventivo" className="py-24 sm:py-28 bg-[#F4F6F8]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Il servizio più richiesto</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A] mb-4">
            Dalle misure di cantiere al preventivo, in pochi secondi
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Tre passaggi, invece di una serata a ricopiare misure. Il lavoro noioso lo fa l'intelligenza artificiale (IA); tu controlli il risultato e decidi.
          </p>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="how-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={i} className="relative bg-white rounded-xl p-6 sm:p-8 border border-[#E2E8F0]">
                <span className="absolute top-6 right-6 font-outfit text-5xl font-bold text-[#0033A0]/10" aria-hidden="true">{i + 1}</span>
                <div className="w-12 h-12 rounded-lg bg-[#0033A0] text-white flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A] mb-2">{s.title}</h3>
                <p className="text-base text-[#4A5568] leading-relaxed">{s.desc}</p>
              </li>
            );
          })}
        </ol>
        <div className="mt-10 max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left" data-testid="send-survey-box">
          <div className="w-14 h-14 shrink-0 rounded-full bg-[#25D366]/15 text-[#128C7E] flex items-center justify-center">
            <WhatsAppIcon size={28} />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A]">Provalo con un tuo sopralluogo</h3>
            <p className="text-base text-[#4A5568]">Mandaci su WhatsApp la foto di un foglio di misure: durante la chiamata ti mostriamo il preventivo preparato dall'intelligenza artificiale.</p>
          </div>
          <a href={SEND_SURVEY_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002880] text-white font-semibold px-6 py-3 rounded-lg" data-testid="send-survey-cta">
            Invia una foto
          </a>
        </div>
      </div>
    </Section>
  );
}

// The AI speaks the trade's language and learns
const CODES = ['F1', 'F2', 'F3', 'PF1', 'PF2', 'PF3', 'PI', 'SC', 'SR', 'TAP', 'PER', 'ZAN', 'FRA', 'CASS', 'MONO'];

const AI_POINTS = [
  { icon: PenLine, title: 'Le tue sigle, non solo le nostre', desc: 'Inseriamo nel vocabolario le abbreviazioni della tua azienda. Quando l\'IA trova una sigla che non conosce, ti propone di insegnargliela.' },
  { icon: GraduationCap, title: 'Impara dalle tue correzioni', desc: 'Correggi una voce con un tocco: il preventivo si aggiorna e ricalcola subito, e l\'IA userà la correzione nei sopralluoghi successivi.' },
  { icon: TriangleAlert, title: 'Ti dice quando non è sicura', desc: 'Le misure scritte male o insolite vengono evidenziate come "da verificare", così sai esattamente cosa ricontrollare prima di inviare.' },
  { icon: BadgeCheck, title: 'Prezzi dal tuo listino', desc: 'Prezzo al m² per prodotto e profilo, accessori al metro, al m² o al pezzo, extra per vetro, apertura e colore. I prezzi scritti sul foglio hanno la precedenza.' },
];

function AiDetails() {
  return (
    <Section className="py-24 sm:py-28 bg-[#0A0F1A] text-white relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#0033A0]/40 rounded-full blur-3xl" aria-hidden="true" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7FA2FF] mb-3">Un'intelligenza artificiale che parla da serramentista</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight mb-5">
              Capisce come scrivi in cantiere. E migliora ogni volta che la correggi.
            </h2>
            <p className="text-base text-white/70 leading-relaxed mb-8">
              Larghezza per altezza, centimetri convertiti in millimetri, "3x PF2" letto come tre pezzi,
              la linea tratteggiata del pavimento che distingue una portafinestra da una finestra.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="ai-codes">
              {CODES.map(c => (
                <span key={c} className="font-mono text-sm px-3 py-1.5 rounded-md bg-white/10 border border-white/10">{c}</span>
              ))}
              <span className="text-sm px-3 py-1.5 rounded-md border border-dashed border-white/30 text-white/70">+ le tue sigle</span>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {AI_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={i} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <Icon size={22} className="text-[#7FA2FF] mb-4" />
                  <h3 className="font-outfit text-base font-semibold mb-2">{p.title}</h3>
                  <p className="text-base text-white/75 leading-relaxed">{p.desc}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Section>
  );
}

// The software behind the services
const FEATURES = [
  { icon: Calculator, title: 'Preventivi su misura', desc: 'Costruttore di preventivi con 156 icone prodotto per finestre, porte, persiane e scorrevoli. PDF con il tuo logo.' },
  { icon: Layers, title: 'Accessori col tuo prezzo', desc: 'Cielini, davanzali, trasporto e posa nella tua libreria prodotti, al metro, al m² o al pezzo.' },
  { icon: Kanban, title: 'Clienti e trattative', desc: 'Dalla prima telefonata alla firma, ogni trattativa sotto controllo, con i solleciti al momento giusto.' },
  { icon: Smartphone, title: 'App per il cantiere', desc: 'Si installa su iPhone e Android: scatti il sopralluogo e consulti i preventivi ovunque.' },
  { icon: MessageSquare, title: 'Notifiche automatiche', desc: 'Messaggi WhatsApp e notifiche sul telefono per solleciti, appuntamenti e un riepilogo ogni mattina.' },
  { icon: Wand2, title: 'Aggiunta rapida con IA', desc: 'Nel preventivo scrivi "PF2 120x220 pvc noce" e la voce viene compilata da sola.' },
];

function Software() {
  return (
    <Section id="software" className="py-24 sm:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Il gestionale Infismart</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A] mb-4">
            Lo strumento su cui costruiamo le tue automazioni
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Lo configuriamo noi per la tua azienda e lo usi dal computer, dal tablet o dal telefono.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="features-grid">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className="group bg-[#F4F6F8] hover:bg-white border border-transparent hover:border-[#E2E8F0] rounded-xl p-6 sm:p-8 transition-all duration-300 hover:shadow-lg"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#0033A0]/10 flex items-center justify-center mb-5 group-hover:bg-[#0033A0] transition-colors duration-300">
                  <Icon size={22} className="text-[#0033A0] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A] mb-2">{f.title}</h3>
                <p className="text-base text-[#4A5568] leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

// First contact offer, with the self-service plan as an alternative
function Offer() {
  return (
    <Section id="prezzi" className="py-24 sm:py-28 bg-[#F4F6F8]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Come iniziare</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A] mb-4">
            Il primo passo è gratuito
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Prima capiamo insieme dove perdi tempo, poi ti facciamo una proposta chiara. Decidi tu se andare avanti.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6" data-testid="offer-grid">
          <div className="rounded-xl p-8 bg-white border-2 border-[#0033A0] shadow-xl relative flex flex-col" data-testid="offer-call">
            <span className="absolute -top-3.5 left-8 bg-[#F59E0B] text-[#0A0F1A] text-xs font-bold px-4 py-1.5 rounded-full shadow-sm">Consigliato</span>
            <h3 className="font-outfit text-xl font-semibold text-[#0A0F1A]">Analisi gratuita con un esperto</h3>
            <p className="font-outfit text-3xl font-bold text-[#0A0F1A] mt-3 mb-5">Gratis <span className="text-base font-normal text-[#4A5568]">e senza impegno</span></p>
            <ul className="space-y-3 flex-1 mb-8">
              {['Chiamata di circa 30 minuti, quando preferisci',
                'Vediamo insieme le attività che ti fanno perdere più tempo',
                'Prova dal vivo sul tuo foglio di sopralluogo',
                'Proposta su misura, con un prezzo chiaro prima di iniziare'].map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check size={18} className="mt-0.5 flex-shrink-0 text-[#0033A0]" />
                  <span className="text-base text-[#4A5568]">{f}</span>
                </li>
              ))}
            </ul>
            <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-lg font-semibold bg-[#0033A0] text-white hover:bg-[#002880] shadow-lg shadow-[#0033A0]/20" data-testid="offer-call-cta">
              <WhatsAppIcon size={18} /> Prenota su WhatsApp
            </a>
          </div>
          <div className="rounded-xl p-8 bg-white border border-[#E2E8F0] flex flex-col" data-testid="offer-software">
            <h3 className="font-outfit text-xl font-semibold text-[#0A0F1A]">Preferisci fare da solo?</h3>
            <p className="text-base text-[#4A5568] mt-1 mb-5">Il gestionale Infismart con il preventivo dalle misure.</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="font-outfit text-3xl font-bold text-[#0A0F1A]">{'\u20AC'}15</span>
              <span className="text-base text-[#4A5568]">/mese</span>
            </div>
            <ul className="space-y-3 flex-1 mb-8">
              {['Preventivo dalle misure con l\'IA', 'Prezzi dal tuo listino', 'Clienti, trattative e solleciti', '7 giorni di prova gratuita'].map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check size={18} className="mt-0.5 flex-shrink-0 text-green-600" />
                  <span className="text-base text-[#4A5568]">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/register?plan=preventivi_ai" className="w-full text-center py-3.5 rounded-lg font-semibold border border-[#E2E8F0] text-[#0A0F1A] hover:border-[#0033A0] hover:text-[#0033A0] transition-all" data-testid="offer-software-cta">
              Prova gratis 7 giorni
            </Link>
          </div>
        </div>
      </div>
    </Section>
  );
}

// FAQ
const FAQS = [
  { q: 'Cosa vuol dire "automatizzare" per un serramentista?', a: 'Far fare a un programma le attività che ripeti sempre uguali: ricopiare misure nel preventivo, mandare i solleciti, aggiornare i prezzi dei fornitori, ricordare gli appuntamenti. Tu resti al centro: controlli e decidi, ma senza perdere ore.' },
  { q: 'Devo essere bravo con il computer?', a: 'No. Impostiamo tutto noi: listino, sigle, messaggi. Ti mostriamo come si usa passo per passo e, se hai un dubbio, ci scrivi su WhatsApp.' },
  { q: 'Quanto costa?', a: 'La prima chiamata di analisi è gratuita. Dopo ti facciamo una proposta con un prezzo chiaro, in base a cosa vuoi automatizzare. Se preferisci usare solo il gestionale da solo, parte da €15 al mese con 7 giorni di prova.' },
  { q: "E se l'intelligenza artificiale sbaglia una misura?", a: 'Le misure poco leggibili o insolite vengono segnalate come "da verificare". Correggi la voce con un tocco: il preventivo si ricalcola subito e l\'IA impara la correzione per le volte successive. Prima di inviare il preventivo lo controlli sempre tu.' },
  { q: 'Funziona con la mia calligrafia e con i miei schizzi?', a: 'Legge appunti a mano, schizzi con ante e aperture, note del telefono e PDF, anche scansionati. Il modo migliore per scoprirlo è provarlo: mandaci la foto di un tuo sopralluogo su WhatsApp.' },
];

function Faq() {
  return (
    <Section id="faq" className="py-24 sm:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0033A0] mb-3">Domande frequenti</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0F1A]">Prima di sentirci</h2>
        </div>
        <div className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <details key={i} className="group py-5" data-testid={`faq-${i}`}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-outfit text-base sm:text-lg font-medium text-[#0A0F1A]">
                {f.q}
                <ChevronRight size={18} className="shrink-0 text-[#0033A0] transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-base sm:text-lg text-[#4A5568] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </Section>
  );
}

// Bottom CTA Section
function BottomCTA() {
  return (
    <Section className="py-24 sm:py-32 bg-[#0033A0] relative overflow-hidden">
      {/* Background shapes */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10 text-center">
        <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-4" data-testid="cta-h2">
          Raccontaci cosa ti fa perdere tempo.
        </h2>
        <p className="text-base sm:text-lg text-white/75 max-w-xl mx-auto mb-10">
          Una chiamata gratuita di mezz'ora per capire cosa può fare l'intelligenza artificiale per la tua azienda di serramenti.
        </p>
        <a
          href={BOOK_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0F1A] font-bold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          data-testid="cta-bottom-btn"
        >
          <WhatsAppIcon size={20} />
          Prenota la chiamata gratuita
        </a>
        <p className="text-white/70 text-sm mt-5">oppure scrivici al <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-white underline">{WHATSAPP_DISPLAY}</a></p>
      </div>
    </Section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="bg-[#0A0F1A] pt-16 pb-8" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-12 border-b border-white/10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <svg viewBox="0 0 32 32" className="w-7 h-7" fill="none">
                <rect x="2" y="2" width="28" height="28" rx="4" fill="#0033A0"/>
                <rect x="6" y="6" width="8" height="9" rx="1" fill="white" opacity="0.9"/>
                <rect x="18" y="6" width="8" height="9" rx="1" fill="white" opacity="0.7"/>
                <rect x="6" y="18" width="8" height="8" rx="1" fill="white" opacity="0.7"/>
                <rect x="18" y="18" width="8" height="8" rx="1" fill="white" opacity="0.5"/>
              </svg>
              <span className="font-outfit text-lg font-bold text-white">Infismart</span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              Automazioni con l'intelligenza artificiale per le aziende di serramenti: preventivi dalle misure di cantiere, solleciti, fornitori e appuntamenti.
            </p>
          </div>

          {/* Servizi */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-4">Servizi</h4>
            <ul className="space-y-2.5">
              {[['Preventivo dalle misure', '#preventivo'], ['Tutti i servizi', '#servizi'], ['Come lavoriamo', '#come-lavoriamo'], ['Il gestionale', '#software']].map(([l, href]) => (
                <li key={l}><a href={href} className="text-sm text-white/50 hover:text-white transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>

          {/* Contatti */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-4">Contatti</h4>
            <ul className="space-y-2.5">
              <li>
                <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                  <WhatsAppIcon size={14} /> WhatsApp {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li><Link to="/login" className="text-sm text-white/50 hover:text-white transition-colors">Area clienti</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">{'\u00A9'} {new Date().getFullYear()} Infismart. Tutti i diritti riservati.</p>
        </div>
      </div>
    </footer>
  );
}

// Always-visible WhatsApp button (a big, familiar target for the first contact)
function FloatingWhatsApp() {
  return (
    <a
      href={BOOK_CALL_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
      aria-label="Scrivici su WhatsApp"
      data-testid="floating-whatsapp"
    >
      <WhatsAppIcon size={30} />
    </a>
  );
}

// Main Landing Page
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />
      <Hero />
      <Pains />
      <Services />
      <HowWeWork />
      <QuoteFromMeasures />
      <AiDetails />
      <Software />
      <Offer />
      <Faq />
      <BottomCTA />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
